import type {AdvancedGeoData} from "~/services/ApiTypes";
import type {Option} from "~/components/visualization/api-dropdown-filter/ApiDropdownTypes";
import type {Country} from "~/components/visualization/bubble-map/countries";
import {countryToContinent} from "~/components/visualization/bubble-map/countries";
import {CountryFlag} from "~/pages/spend-landscape/components/CountryFlag";

/**
 * Country and continent options of the filter bar, from the map data that is already loaded.
 */

function matches(option: Option, search: string) {
    return !search || option.label.toLowerCase().includes(search.toLowerCase());
}

/** Countries come from the map data that is already loaded */
export function countryOptions(geoData: AdvancedGeoData[], search: string): Option[] {
    return geoData
        .map(d => ({
            id: d.country,
            label: d.country || '-',
            icon: d.country ? <CountryFlag code={d.country}/> : undefined,
            subTotalSpend: d.spend,
            obj: d,
        }))
        .filter(o => matches(o, search));
}

export function continentOptions(geoData: AdvancedGeoData[], search: string): Option[] {
    const spend = new Map<string, number>();
    geoData.forEach(d => {
        const continent = countryToContinent(d.country as Country) || 'Unknown';
        spend.set(continent, (spend.get(continent) ?? 0) + d.spend);
    });
    return Array.from(spend.entries())
        .sort((a, b) => b[1] - a[1])
        .map(([continent, total]) => ({id: continent, label: continent, subTotalSpend: total, obj: continent}))
        .filter(o => matches(o, search));
}

/** The countries of the given continents */
export function countriesOfContinents(geoData: AdvancedGeoData[], continents: string[]): string[] {
    return geoData
        .filter(d => continents.includes(countryToContinent(d.country as Country) || 'Unknown'))
        .map(d => d.country);
}

/** The continents whose countries are all selected */
export function continentsOfCountries(geoData: AdvancedGeoData[], countries: string[] | undefined): string[] {
    if (!countries || countries.length === 0) return [];
    const byContinent = new Map<string, string[]>();
    geoData.forEach(d => {
        const continent = countryToContinent(d.country as Country) || 'Unknown';
        byContinent.set(continent, [...(byContinent.get(continent) ?? []), d.country]);
    });
    return Array.from(byContinent.entries())
        .filter(([, cs]) => cs.every(c => countries.includes(c)))
        .map(([continent]) => continent);
}
